var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __async = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};

// src/index.ts
import {
  subscribeToConfig as subscribeToConfig2,
  setConfig as setConfig2,
  useConfigSelection as useConfigSelection2,
  useUnknownConfigProperty as useUnknownConfigProperty2,
  useConfigProperty as useConfigProperty2,
  getConfig as getConfig3,
  getSettledConfig,
  isConfigSettled
} from "@leagueplatform/config";
import {
  AppEnvironment,
  ENVIRONMENT,
  getEnvironment,
  isDev,
  isNodeTestEnv as isNodeTestEnv2,
  isProduction,
  isStaging,
  isStorybook
} from "@leagueplatform/app-environment";
import {
  overwriteRoutePaths,
  getModulePath,
  LEAGUE_MODULE_NAMES
} from "@leagueplatform/league-routes";

// src/utils/server-config.ts
import {
  getConfig,
  MODULE_VERSION,
  subscribeToConfig,
  updateServerConfig
} from "@leagueplatform/config";
import { captureError } from "@leagueplatform/observability";

// src/utils/get-browser-info.ts
var userAgentBrowsersRule = [
  { name: "android", regex: /Android\s([0-9.]+)/ },
  { name: "chrome", regex: /(?!Chrom.*OPR)Chrom(?:e|ium)\/([0-9.]+)(:?\s|$)/ },
  {
    name: "chromium-webview",
    regex: /(?!Chrom.*OPR)wv\).*Chrom(?:e|ium)\/([0-9.]+)(:?\s|$)/
  },
  { name: "edge", regex: /Edge\/([0-9._]+)/ },
  { name: "edge-chromium", regex: /EdgA?\/([0-9.]+)/ },
  { name: "edge-ios", regex: /EdgiOS\/([0-9._]+)/ },
  { name: "firefox", regex: /Firefox\/([0-9.]+)(?:\s|$)/ },
  { name: "ios", regex: /Version\/([0-9._]+).*Mobile.*Safari.*/ },
  { name: "ios-webview", regex: /AppleWebKit\/([0-9.]+).*Mobile/ },
  { name: "ios-webview", regex: /AppleWebKit\/([0-9.]+).*Gecko\)$/ },
  { name: "opera", regex: /Opera\/([0-9.]+)(?:\s|$)/ },
  { name: "opera", regex: /OPR\/([0-9.]+)(:?\s|$)/ },
  { name: "opera-mini", regex: /Opera Mini.*Version\/([0-9.]+)/ },
  { name: "safari", regex: /Version\/([0-9._]+).*Safari/ },
  { name: "samsung", regex: /SamsungBrowser\/([0-9.]+)/ }
];
var getBrowserInfo = () => {
  if (typeof window === "undefined")
    return { name: void 0, version: void 0 };
  const { userAgent } = navigator;
  return userAgentBrowsersRule.reduce(
    (result, browserData) => {
      if (result.name)
        return result;
      const match = userAgent.match(browserData.regex);
      if (match) {
        return { name: browserData.name, version: match[1] };
      }
      return result;
    },
    { name: void 0, version: void 0 }
  );
};

// src/utils/server-config.ts
var SERVER_CONFIG_API_VERSION = "v1";
function callbackError() {
  const { config } = getConfig();
  if (config.core.onError) {
    const error = new Error(
      "Failed to fetch League configuration, and no cached configuration was found."
    );
    error.name = "Failed to load League configuration";
    config.core.onError(error);
  }
}
function createServerConfigUpdateSubscription() {
  subscribeToConfig(
    /**
     * Notice that subscribing to only the parameters we need means that the eventual
     * call to `updateServerConfig` will only happen when those parameters change,
     * which is exactly the circumstances under which we want to get (or re-get)
     * server config values. This also prevents an infinite loop of config setting,
     * since the act of setting `serverConfig` will not in itself re-trigger this subscription,
     * because it does not involve modifying any of the client config parameters we
     * are subscribed to.
     */
    ({ config }) => {
      var _a;
      return {
        appEnvironment: config.core.appEnvironment,
        clientId: config.core.clientId,
        appVersion: config.core.appVersion,
        // eslint-disable-next-line no-underscore-dangle
        isNodeTestEnv: (_a = config.__internal) == null ? void 0 : _a.isNodeTestEnv
      };
    },
    (selection) => {
      if (!selection || selection.isNodeTestEnv) {
        return;
      }
      const { name: browserName, version: browserVersion } = getBrowserInfo();
      updateServerConfig({
        clientId: selection.clientId,
        environment: selection.appEnvironment,
        apiVersion: SERVER_CONFIG_API_VERSION,
        browserName,
        browserVersion,
        sdkVersion: MODULE_VERSION,
        appVersion: selection.appVersion
      }).then().catch((error) => {
        callbackError();
        captureError(error, { errorName: "Get Server Config Error" });
      });
    }
  );
}

// src/components/league-provider.tsx
import React5, { memo, useEffect as useEffect3 } from "react";
import { ObservabilityErrorBoundary } from "@leagueplatform/observability";
import { LeagueIntlProvider } from "@leagueplatform/locales";
import { ToastManager } from "@leagueplatform/toast-messages";
import { isNodeTestEnv } from "@leagueplatform/app-environment";
import {
  setConfig,
  getConfig as getConfig2,
  LeagueConfigError,
  ConfigStoreStatus,
  useConfigSelection
} from "@leagueplatform/config";
import { SessionTimeout } from "@leagueplatform/session-timeout";
import { StandaloneAuth } from "@leagueplatform/auth-standalone";
import { Auth, Implementation } from "@leagueplatform/auth";

// src/components/league-theme-provider.tsx
import React from "react";
import {
  formatThemeForCommons,
  GenesisThemeProvider
} from "@leagueplatform/genesis-commons";
import { UIKitThemeProvider } from "@leagueplatform/ui-kit";
import {
  GDSThemeProvider,
  loadTheme
} from "@leagueplatform/genesis-core";
import {
  useConfigProperty,
  useUnknownConfigProperty
} from "@leagueplatform/config";
var LeagueThemeProvider = ({ children }) => {
  const { theme, themeOptions } = useConfigProperty("core.ui");
  const coreThemeOptions = __spreadValues({
    useRems: false
  }, themeOptions);
  const coreTheme = loadTheme(theme, coreThemeOptions);
  const shouldApplyCSSReset = useUnknownConfigProperty(
    "__internal.shouldApplyCSSReset"
  );
  const shouldApplyLegacyGlobalStyles = useUnknownConfigProperty(
    "__internal.shouldApplyLegacyGlobalStyles"
  );
  if ((themeOptions == null ? void 0 : themeOptions.noLegacyTheming) === true) {
    return /* @__PURE__ */ React.createElement(GDSThemeProvider, { theme: coreTheme }, children);
  }
  const commonsTheme = formatThemeForCommons(theme);
  return /* @__PURE__ */ React.createElement(GDSThemeProvider, { theme: coreTheme }, /* @__PURE__ */ React.createElement(GenesisThemeProvider, { theme: commonsTheme }, /* @__PURE__ */ React.createElement(
    UIKitThemeProvider,
    {
      shouldApplyCSSReset,
      shouldApplyLegacyGlobalStyles
    },
    children
  )));
};

// src/components/react-query-provider-wrapper.tsx
import React4, { useContext, useEffect as useEffect2, useRef as useRef2 } from "react";
import {
  MutationCache,
  QueryClient,
  QueryClientProvider
} from "@tanstack/react-query";
import { getIsImpersonator } from "@leagueplatform/auth-utils";
import { LocaleContext } from "@leagueplatform/locales";
import { SocketAsFetch } from "@leagueplatform/socket-as-fetch";

// src/components/permissions-error-modal/permissions-error-modal.provider.tsx
import React3, {
  useCallback,
  useState,
  useEffect,
  useRef
} from "react";
import {
  useInRouterContext,
  useLocationLegacy as useLocation
} from "@leagueplatform/routing";

// src/components/permissions-error-modal/permissions-error-modal.component.tsx
import React2 from "react";
import {
  HeadingText,
  Modal,
  StackLayout,
  Image,
  Button,
  HeadingBar,
  StackItem,
  ParagraphText
} from "@leagueplatform/genesis-core";
import { useIntl } from "@leagueplatform/locales";
import { useStaticAsset } from "@leagueplatform/asset-config";
var PermissionsErrorModal = ({
  onModalOpenChange,
  isActionError
}) => {
  const { formatMessage } = useIntl();
  const errorModalImage = useStaticAsset(
    "WEB_COMMON_ERROR_STATE_IMAGE" /* WEB_COMMON_ERROR_STATE_IMAGE */
  );
  const message = isActionError ? formatMessage({ id: "CSE_RESTRICTED_ACTION_MESSAGE" }) : formatMessage({ id: "CSE_RESTRICTED_VIEW_MESSAGE" });
  return /* @__PURE__ */ React2.createElement(StackLayout, null, /* @__PURE__ */ React2.createElement(Modal.Root, { open: true, onOpenChange: onModalOpenChange }, /* @__PURE__ */ React2.createElement(Modal.Content, { showCloseButton: false, padding: "$none" }, /* @__PURE__ */ React2.createElement(
    HeadingBar,
    {
      trailingAction: /* @__PURE__ */ React2.createElement(Modal.Close, null, /* @__PURE__ */ React2.createElement(
        Button,
        {
          priority: "tertiary",
          quiet: true,
          icon: "tinyClose",
          hideLabel: true,
          size: "small"
        },
        formatMessage({ id: "CLOSE" })
      )),
      title: /* @__PURE__ */ React2.createElement(Modal.Title, null, /* @__PURE__ */ React2.createElement(
        HeadingText,
        {
          size: "sm",
          level: "2",
          css: { typography: "$subtitleOne" }
        },
        formatMessage({ id: "CSE_RESTRICTED_ACTION" })
      ))
    }
  ), /* @__PURE__ */ React2.createElement(
    StackLayout,
    {
      horizontalAlignment: "center",
      css: { padding: "$oneAndHalf" }
    },
    /* @__PURE__ */ React2.createElement(
      Image,
      {
        width: 120,
        height: 120,
        css: { paddingBottom: "$one" },
        src: errorModalImage
      }
    ),
    /* @__PURE__ */ React2.createElement(Modal.Description, null, /* @__PURE__ */ React2.createElement(
      ParagraphText,
      {
        css: { typography: "$bodyOne", textAlign: "center" }
      },
      message
    ))
  ), /* @__PURE__ */ React2.createElement(StackItem, { css: { padding: "$none $oneAndHalf $one" } }, /* @__PURE__ */ React2.createElement(Modal.Close, null, /* @__PURE__ */ React2.createElement(Button, { width: "fillContainer", priority: "primary", size: "large" }, formatMessage({ id: "GOT_IT" })))))));
};

// src/components/permissions-error-modal/permissions-error-modal.provider.tsx
var stateSetters = {
  setMutationWasReported: () => {
  },
  setErrorWasReported: () => {
  },
  setIsPermissionError: () => {
  }
};
var reportAgentPermissionError = (reportOnlyOnce, permissionsErrorMessage) => {
  if (reportOnlyOnce) {
    stateSetters.setMutationWasReported(true);
  } else {
    stateSetters.setErrorWasReported(true);
  }
  if (permissionsErrorMessage) {
    stateSetters.setIsPermissionError(true);
  }
};
var PermissionsErrorModalProviderInner = ({
  pathname,
  children
}) => {
  const [errorWasReported, setErrorWasReported] = useState(false);
  const [mutationWasReported, setMutationWasReported] = useState(false);
  const [isPermissionsError, setIsPermissionError] = useState(false);
  const initialPathname = useRef(pathname);
  stateSetters.setErrorWasReported = setErrorWasReported;
  stateSetters.setMutationWasReported = setMutationWasReported;
  stateSetters.setIsPermissionError = setIsPermissionError;
  const onModalOpenChange = useCallback((isOpen) => {
    if (!isOpen) {
      setMutationWasReported(false);
      setErrorWasReported(false);
    }
  }, []);
  const shouldShowModal = mutationWasReported || errorWasReported;
  useEffect(() => {
    const unregister = () => {
      setErrorWasReported(false);
      setIsPermissionError(false);
    };
    if (pathname !== initialPathname.current) {
      unregister();
    }
    return () => {
      unregister();
    };
  }, [pathname]);
  return /* @__PURE__ */ React3.createElement(React3.Fragment, null, children, shouldShowModal && /* @__PURE__ */ React3.createElement(
    PermissionsErrorModal,
    {
      onModalOpenChange,
      isActionError: mutationWasReported && !isPermissionsError
    }
  ));
};
var InRouterWrapper = ({ children }) => {
  const { pathname } = useLocation();
  return /* @__PURE__ */ React3.createElement(PermissionsErrorModalProviderInner, { pathname }, children);
};
var PermissionsErrorModalProvider = ({
  children
}) => {
  const isInRouter = useInRouterContext();
  if (isInRouter) {
    return /* @__PURE__ */ React3.createElement(InRouterWrapper, null, children);
  }
  return /* @__PURE__ */ React3.createElement(PermissionsErrorModalProviderInner, { pathname: "" }, children);
};

// src/components/react-query-provider-wrapper.tsx
var queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError(error) {
      return __async(this, null, function* () {
        const isImpersonationSession = yield getIsImpersonator();
        if (error instanceof Error) {
          const isWSImpersonatorError = error.message.includes("unauthorized");
          const isRESTImpersonatorError = error.message.includes("403");
          const showImpersonationError = isImpersonationSession && (isRESTImpersonatorError || isWSImpersonatorError);
          if (showImpersonationError) {
            reportAgentPermissionError(true);
          }
        }
      });
    }
  }),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 3
    }
  }
});
var ReactQueryProviderWrapper = ({
  children
}) => {
  const { locale } = useContext(LocaleContext);
  const prevLocale = useRef2(locale);
  useEffect2(() => {
    if (locale !== prevLocale.current) {
      SocketAsFetch.updateUserConnectionConfiguration().then(() => {
        queryClient.invalidateQueries();
      });
      prevLocale.current = locale;
    }
  }, [locale]);
  return /* @__PURE__ */ React4.createElement(QueryClientProvider, { client: queryClient }, children);
};

// src/components/league-provider.tsx
var defaultOnSessionExpire = () => {
  StandaloneAuth.client.logout({
    returnTo: window.location.origin
  });
};
var defaultOnSessionExtend = () => {
  StandaloneAuth.client.getTokenSilently({
    cacheMode: "off"
  });
};
var NO_CONFIG_ERROR = "Config was neither set before mounting `<LeagueProvider>` nor passed to its `config` prop. You must do one or the other!";
var InnerProviders = memo(({ children }) => {
  const {
    sessionTimeoutInterval,
    onSessionExpire,
    onSessionExtend,
    forceIdleSessionLogout
  } = useConfigSelection(({ config }) => {
    var _a, _b, _c;
    const authConfig = config.core.auth;
    if (!authConfig || !("clientOptions" in authConfig)) {
      return {
        sessionTimeoutInterval: Infinity,
        onSessionExpire: defaultOnSessionExtend,
        onSessionExtend: defaultOnSessionExtend,
        forceIdleSessionLogout: false
      };
    }
    return {
      sessionTimeoutInterval: authConfig.idleSessionLifetimeHours * 36e5,
      onSessionExpire: ((_a = authConfig.sessionTimeoutOptions) == null ? void 0 : _a.onSessionExpire) || defaultOnSessionExpire,
      onSessionExtend: ((_b = authConfig.sessionTimeoutOptions) == null ? void 0 : _b.onSessionExtend) || defaultOnSessionExtend,
      forceIdleSessionLogout: ((_c = authConfig.sessionTimeoutOptions) == null ? void 0 : _c.forceIdleSessionLogout) || false
    };
  });
  const contentsWithoutErrorBoundary = /* @__PURE__ */ React5.createElement(LeagueThemeProvider, null, /* @__PURE__ */ React5.createElement(LeagueIntlProvider, null, /* @__PURE__ */ React5.createElement(ReactQueryProviderWrapper, null, /* @__PURE__ */ React5.createElement(PermissionsErrorModalProvider, null, children, !isNodeTestEnv() && /* @__PURE__ */ React5.createElement(ToastManager, null), !isNodeTestEnv() && Auth.initialized && Auth.implementation === Implementation.STANDALONE && /* @__PURE__ */ React5.createElement(
    SessionTimeout,
    {
      timeoutInterval: sessionTimeoutInterval,
      getIsAuthenticated: Auth.isAuthorized,
      onSessionExpire,
      onSessionExtend,
      forceIdleSessionLogout
    }
  )))));
  return isNodeTestEnv() ? contentsWithoutErrorBoundary : /* @__PURE__ */ React5.createElement(ObservabilityErrorBoundary, null, contentsWithoutErrorBoundary);
});
var LeagueProvider = ({
  children,
  config,
  treatConfigAsInitialOnly
}) => {
  if (getConfig2(false).status === ConfigStoreStatus.Empty) {
    if (!config) {
      throw new LeagueConfigError(NO_CONFIG_ERROR);
    }
    setConfig(config);
  }
  useEffect3(() => {
    if (config && !treatConfigAsInitialOnly) {
      setConfig(config);
    }
  }, [config, treatConfigAsInitialOnly]);
  useEffect3(
    () => (
      /**
       * Invalidate all queries when LeagueProvider unmounts to ensure fresh data
       * on remount. This prevents the UI from momentarily displaying stale content
       * (especially content from a previous locale) before new requests complete.
       */
      () => {
        queryClient.clear();
      }
    ),
    []
  );
  return /* @__PURE__ */ React5.createElement(InnerProviders, null, children);
};

// src/utils/api-requests.ts
import { leagueFetch } from "@leagueplatform/league-fetch";
import { SocketAsFetch as SocketAsFetch2 } from "@leagueplatform/socket-as-fetch";
function wsSend(message_type, info) {
  return __async(this, null, function* () {
    var _a;
    try {
      const response = yield SocketAsFetch2.fetch({ message_type, info });
      console.log(`\u{1F680} "${message_type}": success`);
      return response;
    } catch (error) {
      if (error instanceof Error) {
        const errorMessage = JSON.parse(error.message);
        console.error(
          `\u{1F494} "${message_type}": we had a problem with this api call: ${(_a = errorMessage == null ? void 0 : errorMessage.info) == null ? void 0 : _a.reason}`
        );
        console.error(error);
        return errorMessage;
      }
      throw error;
    }
  });
}
function RESTRequest(path, method = "GET", body) {
  return __async(this, null, function* () {
    let response;
    try {
      response = yield leagueFetch(path, {
        method,
        body
      });
    } catch (ex) {
      console.error(`window.league.api.rest ${method} failure`, ex);
      throw ex;
    }
    if (!response.ok) {
      console.error(
        `window.league.api.rest ${method} error response`,
        response.status,
        response.statusText
      );
    }
    return response;
  });
}
var LeagueConsoleAPI = {
  api: {
    ws: wsSend,
    send: wsSend,
    // legacy name for WS handler
    rest: RESTRequest
  }
};
function setupLeagueAPI() {
  if (typeof window !== "undefined" && !window.league) {
    window.league = LeagueConsoleAPI;
  }
}

// src/utils/query-client-methods.ts
var invalidateQueries = (...args) => queryClient.invalidateQueries(
  ...args
);
var removeQueries = (...args) => queryClient.removeQueries(
  ...args
);

// src/hooks/use-invalidate-queries.ts
import { useCallback as useCallback2 } from "react";
function useInvalidateQueries(queryFilters) {
  return useCallback2(() => {
    invalidateQueries(queryFilters);
  }, [queryFilters]);
}

// src/hooks/use-remove-queries.ts
import { useCallback as useCallback3 } from "react";
function useRemoveQueries(queryFilters) {
  return useCallback3(() => {
    removeQueries(queryFilters);
  }, [queryFilters]);
}

// src/index.ts
createServerConfigUpdateSubscription();
setupLeagueAPI();
var src_default = {
  setConfig: setConfig2,
  subscribeToConfig: subscribeToConfig2,
  useConfigSelection: useConfigSelection2,
  useUnknownConfigProperty: useUnknownConfigProperty2,
  useConfigProperty: useConfigProperty2,
  getConfig: getConfig3,
  getSettledConfig,
  isConfigSettled,
  LeagueProvider,
  getModulePath,
  LEAGUE_MODULE_NAMES,
  AppEnvironment,
  getEnvironment,
  isDev,
  isProduction,
  isStaging,
  isNodeTestEnv: isNodeTestEnv2,
  isStorybook,
  ENVIRONMENT,
  overwriteRoutePaths,
  reportAgentPermissionError,
  invalidateQueries,
  removeQueries,
  useInvalidateQueries,
  useRemoveQueries
};
export {
  AppEnvironment,
  ENVIRONMENT,
  LEAGUE_MODULE_NAMES,
  LeagueProvider,
  src_default as default,
  getConfig3 as getConfig,
  getEnvironment,
  getModulePath,
  getSettledConfig,
  invalidateQueries,
  isConfigSettled,
  isDev,
  isNodeTestEnv2 as isNodeTestEnv,
  isProduction,
  isStaging,
  isStorybook,
  overwriteRoutePaths,
  removeQueries,
  reportAgentPermissionError,
  setConfig2 as setConfig,
  subscribeToConfig2 as subscribeToConfig,
  useConfigProperty2 as useConfigProperty,
  useConfigSelection2 as useConfigSelection,
  useInvalidateQueries,
  useRemoveQueries,
  useUnknownConfigProperty2 as useUnknownConfigProperty
};


  if (typeof window !== 'undefined' && !(typeof process !== 'undefined' && process.env?.NODE_ENV === 'test')) {
    window.leagueSDKsPresent = window.leagueSDKsPresent || [];
    window.leagueSDKsPresent['@leagueplatform/core'] = window.leagueSDKsPresent['@leagueplatform/core'] || [];
    window.leagueSDKsPresent['@leagueplatform/core'].push('2.27.1');

    const numInstances = window.leagueSDKsPresent['@leagueplatform/core'].length

    if (numInstances > 1) {
      console.error('Multiple copies (' + numInstances + ') of the @leagueplatform/core module are loaded! This can cause issues. Ensure that your project bundle only contains one copy/version of each League SDK package.');
    }
  }
  
//# sourceMappingURL=index.js.map